import { setAccessTokenExpiry } from '~/lib/api/access-token-expiry';
import { api, type AuthResponse } from '~/lib/api/client';
import { ApiError } from '~/lib/api/errors';
import type { ChangePasswordInput, LoginInput, ProfileInput, RegisterInput } from './auth.schemas';
import type { Session, User } from './auth.types';

/**
 * Thin, typed bindings for the backend's auth surface. Everything that touches
 * `/auth` or `/users/me` goes through here so the endpoint paths live in one file.
 */

export async function login(input: LoginInput): Promise<User> {
  const result = await api.post<AuthResponse>('/auth/login', input);
  setAccessTokenExpiry(result.expiresIn);
  return result.user;
}

export async function register(input: RegisterInput): Promise<User> {
  const result = await api.post<AuthResponse>('/auth/register', input);
  setAccessTokenExpiry(result.expiresIn);
  return result.user;
}

/** Rename yourself. The account is taken from the token, never from the body. */
export function updateProfile(input: ProfileInput): Promise<User> {
  return api.patch<User>('/users/me', input);
}

/**
 * Change your own password. The server ends every session on the account and
 * issues this device a fresh pair in the response, so the caller stays signed
 * in here and is signed out everywhere else.
 */
export async function changePassword(input: ChangePasswordInput): Promise<User> {
  const result = await api.post<AuthResponse>('/auth/change-password', input);
  setAccessTokenExpiry(result.expiresIn);
  return result.user;
}

export async function logout(): Promise<void> {
  try {
    await api.post<void>('/auth/logout');
  } catch (error) {
    // The session is being torn down either way. A 401 here just means the
    // access token had already lapsed, which is not worth surfacing; the
    // backend has cleared the cookies regardless.
    if (!(error instanceof ApiError) || !error.isUnauthorized) throw error;
  }
}

/** Revoke every session for this user, on every device. */
export async function logoutEverywhere(): Promise<void> {
  await api.post<void>('/auth/logout-all');
}

/**
 * Resolve the current session. A 401 that survives the client's automatic
 * refresh means "signed out" — an ordinary answer, not an error, so it comes
 * back as `null` rather than throwing. Anything else (500, network) does throw,
 * so a backend outage is not silently rendered as a logged-out user.
 */
export async function fetchSession(): Promise<Session> {
  try {
    return await api.get<User>('/users/me');
  } catch (error) {
    if (error instanceof ApiError && error.isUnauthorized) return null;
    throw error;
  }
}
